<template>
  <div class="flex flex-col h-full bg-neutral-50">
    <!-- Styled Header -->
    <div 
      class="px-4 py-3 bg-white" 
      :style="{
        borderBottom: '1px solid #e5e7eb'
      }"
    >
      <div class="flex justify-between items-center">
        <!-- Left side: Empty space for symmetry -->
        <div class="w-10 h-10"></div>
        
        <!-- Center: Title -->
        <div class="text-center flex-1">
          <h1 class="text-xl font-semibold text-neutral-800">Meeting vereinbaren</h1>
        </div>
        
        <!-- Right side: Mobile close button only -->
        <div class="flex items-center">
          <!-- Mobile: Show close button -->
          <button 
            v-if="isMobile" 
            @click="$emit('close-widget')" 
            class="cursor-pointer text-neutral-600 hover:text-neutral-800 transition-colors duration-200"
          >
            <MaCross class="h-5 w-5" />
          </button>
          
          <!-- Desktop: Empty space (no button) -->
          <div v-else class="w-10 h-10"></div>
        </div>
      </div>
    </div>

    <!-- Styled Progress Steps -->
    <div 
      class="px-4 py-3 bg-white" 
      :style="{
        borderBottom: '1px solid #e5e7eb'
      }"
    >
      <div class="flex items-center justify-between">
        <div 
          v-for="(step, index) in steps" 
          :key="index"
          class="flex items-center"
          :class="{ 'flex-1': index < steps.length - 1 }"
        >
          <!-- Step Circle -->
          <div class="flex flex-col items-center">
            <div 
              class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-300"
              :class="[
                currentStep > index 
                  ? 'bg-green-500 text-white' 
                  : currentStep === index 
                    ? 'bg-neutral-700 text-white' 
                    : 'bg-neutral-200 text-neutral-500'
              ]"
            >
              <span v-if="currentStep > index">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <span 
              class="text-xs mt-1 text-center"
              :class="[
                currentStep >= index 
                  ? 'text-neutral-800 font-medium' 
                  : 'text-neutral-400'
              ]"
            >
              {{ step.name }}
            </span>
          </div>
          
          <!-- Progress Line -->
          <div 
            v-if="index < steps.length - 1"
            class="flex-1 h-0.5 mx-2 bg-neutral-200 rounded"
          >
            <div 
              class="h-0.5 bg-neutral-400 rounded transition-all duration-500"
              :style="{ width: currentStep > index ? '100%' : '0%' }"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step Content -->
    <div class="flex-1 overflow-y-auto">
      <transition 
        mode="out-in"
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 transform translate-x-4"
        enter-to-class="opacity-100 transform translate-x-0"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 transform translate-x-0"
        leave-to-class="opacity-0 transform translate-x-[-4px]"
      >
        <component 
          :is="currentStepComponent" 
          :key="currentStep"
          :form-data="formData"
          :styles="styles"
          :available-times="availableTimes"
          :min-date="minDate"
          @update:form-data="updateFormData"
          @next-step="nextStep"
          @prev-step="prevStep"
          @reset-form="resetForm"
        ></component>
      </transition>
    </div>
  </div>
</template>

<script>
import MeetingStep1 from '../components/meetings/MeetingStep1.vue';
import MeetingStep2 from '../components/meetings/MeetingStep2.vue';
import MeetingStep3 from '../components/meetings/MeetingStep3.vue';
import MeetingStep4 from '../components/meetings/MeetingStep4.vue';
import { sendEmail } from '../lib/appwrite';
import { MaCross } from '@kalimahapps/vue-icons';

export default {
  name: 'MeetingScreen',
  components: {
    MeetingStep1,
    MeetingStep2,
    MeetingStep3,
    MeetingStep4,
    MaCross
  },
  props: {
    styles: {
      type: Object,
      default: () => ({
        emailAddress: 'contact@yourcompany.com',
        companyName: 'Chatly'
      })
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close-widget'],
  data() {
    return {
      currentStep: 0,
      steps: [
        { name: 'Zeit', required: ['date', 'time', 'duration'] },
        { name: 'Info', required: ['name', 'email'] },
        { name: 'Details', required: [] },
        { name: 'Abschluss', required: [] }
      ],
      formData: {
        date: '',
        time: '',
        duration: '30',
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        sendReminder: true
      },
      availableTimes: [
        '9:00', '9:30', '10:00', 
        '10:30', '11:00', '11:30',
        '13:00', '13:30', '14:00',
        '14:30', '15:00', '15:30',
        '16:00', '16:30', '17:00'
      ],
      loading: false,
      error: null
    };
  },
  computed: {
    minDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },
    currentStepComponent() {
      const componentMap = {
        0: 'MeetingStep1',
        1: 'MeetingStep2',
        2: 'MeetingStep3',
        3: 'MeetingStep4'
      };
      return componentMap[this.currentStep];
    },
    businessEmail() {
      return this.styles.emailAddress || 'contact@yourcompany.com';
    },
    companyName() {
      return this.styles.companyName || 'Chatly';
    }
  },
  mounted() {
    const today = new Date();
    this.formData.date = today.toISOString().split('T')[0];
    console.log('MeetingScreen mounted with formData:', this.formData);
  },
  methods: {
    updateFormData(updatedData) {
      console.log('Updating form data:', updatedData);
      this.formData = { ...this.formData, ...updatedData };
      console.log('Updated formData:', this.formData);
    },
    
    async nextStep() {
      console.log(`Moving from step ${this.currentStep} to next step`);
      
      if (this.currentStep < this.steps.length - 1) {
        if (this.currentStep === this.steps.length - 2) {
          // This is step 2 (index 2), the final form step before confirmation
          console.log('Submitting form before moving to confirmation step');
          await this.submitForm();
          // submitForm() will handle moving to the next step after successful submission
        } else {
          // For other steps, just move forward normally
          this.currentStep++;
          console.log(`Moved to step ${this.currentStep}`);
        }
      }
    },
    
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
        console.log(`Moved back to step ${this.currentStep}`);
      }
    },
    
    async submitForm() {
      try {
        this.loading = true;
        this.error = null;
        
        console.log('📅 Submitting meeting form with data:', {
          ...this.formData,
          businessEmail: this.businessEmail,
          companyName: this.companyName
        });
        
        // Validate required fields before submission
        if (!this.formData.name || !this.formData.email || !this.formData.date || !this.formData.time) {
          throw new Error('Missing required fields');
        }
        
        // 1. Send confirmation email to customer
        const customerEmailContent = `
Hallo ${this.formData.name},

vielen Dank für Ihre Meeting-Anfrage! Wir haben Ihre Buchung erhalten.

📅 Meeting Details:
• Datum: ${this.formatDate(this.formData.date)}
• Zeit: ${this.formData.time}
• Dauer: ${this.formData.duration} Minuten
${this.formData.company ? `• Unternehmen: ${this.formData.company}` : ''}
${this.formData.phone ? `• Telefon: ${this.formData.phone}` : ''}

${this.formData.message ? `📝 Ihre Nachricht:\n"${this.formData.message}"` : ''}

${this.formData.sendReminder ? '🔔 Sie erhalten eine Erinnerung 1 Stunde vor dem Meeting.' : ''}

Wir freuen uns auf unser Gespräch!

Mit freundlichen Grüßen,
${this.companyName} Team
        `.trim();
        
        console.log('Sending customer confirmation email...');
        await sendEmail({
          to: this.formData.email,
          subject: `✅ Meeting-Bestätigung - Termin gebucht - ${this.companyName}`,
          text: customerEmailContent,
          fromEmail: 'no-reply@chatly-mail.ch',
          fromName: 'Chatly Benachrichtigung'
        });
        
        // 2. Send notification email to business
        const businessEmailContent = `
🗓️ Neue Meeting-Buchung über ${this.companyName} Chat Widget eingegangen

👤 Kontaktdaten:
• Name: ${this.formData.name}
• E-Mail: ${this.formData.email}
${this.formData.phone ? `• Telefon: ${this.formData.phone}` : ''}
${this.formData.company ? `• Unternehmen: ${this.formData.company}` : ''}

📅 Meeting Details:
• Datum: ${this.formatDate(this.formData.date)}
• Zeit: ${this.formData.time}
• Dauer: ${this.formData.duration} Minuten

${this.formData.message ? `📝 Nachricht des Kunden:\n"${this.formData.message}"` : ''}

${this.formData.sendReminder ? '🔔 Kunde möchte eine Erinnerung erhalten.' : ''}

Bitte tragen Sie den Termin in Ihren Kalender ein.

---
Diese Meeting-Buchung wurde über das Chat Widget auf Ihrer Website gesendet.
        `.trim();
        
        console.log('Sending business notification email to:', this.businessEmail);
        await sendEmail({
          to: this.businessEmail,
          subject: '🗓️ Neue Meeting-Buchung',
          text: businessEmailContent,
          fromEmail: 'no-reply@chatly-mail.ch',
          fromName: 'Chatly Benachrichtigung'
        });
        
        console.log('✅ Meeting emails sent successfully');
        
        // Only move to confirmation step after successful email sending
        this.currentStep++;
        console.log(`Successfully moved to confirmation step ${this.currentStep}`);
        
      } catch (error) {
        console.error('❌ Error submitting meeting form:', error);
        this.error = "Fehler beim Buchen des Meetings. Bitte versuchen Sie es erneut.";
        
        // Don't move to next step if there's an error
        console.log('Staying on current step due to error');
      } finally {
        this.loading = false;
      }
    },
    
    resetForm() {
      console.log('Resetting meeting form');
      const today = new Date();
      this.formData = {
        date: today.toISOString().split('T')[0],
        time: '',
        duration: '30',
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        sendReminder: true
      };
      this.currentStep = 0;
      this.error = null;
      this.loading = false;
      console.log('Form reset to initial state:', this.formData);
    },
    
    formatDate(dateString) {
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
      }
    }
  }
};
</script>

<style scoped>
/* Clean scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}
</style>